import type { ChangeEvent } from "react";
import { useEffect, useRef, useState } from "react";
import { styled } from "styled-components";
import { v4 as createUuid } from "uuid";

import { HiddenImageUpload, TextInput, UIButton } from "common/components";
import { Separator } from "common/components/Separator";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { clearCommunityImageUrl, editCommunity, uploadImage } from "domains/collection/actions";
import { getUploadedImages } from "domains/collection/selectors";
import type { Community } from "domains/collection/types";
import type { LocalFile } from "domains/createMapDetail/types";
import { deactivateModal } from "domains/modal/actions";
import { ModalHeader } from "domains/modal/components/ModalHeader";
import { getModalParams } from "domains/modal/selectors";
import type { ModalParams } from "domains/modal/types";

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const StyledImage = styled.img`
  border-radius: 0.25rem;
  height: 4rem;
  object-fit: cover;
  object-position: center;
  width: 4rem;
`;

const StyledTextInput = styled(TextInput)`
  > label:first-child {
    color: ${({ theme }) => theme.colors.textBright};
  }
`;

const StyledTitle = styled.p`
  color: ${({ theme }) => theme.colors.textBright};
  font-weight: bold;
`;

export const CollectionCreateCoCuratorModal = () => {
  const dispatch = useDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const params = useSelector(getModalParams) as ModalParams<"CollectionCreateCoCurator">;
  const uploadedImageUrls = useSelector(getUploadedImages);
  const [name, setName] = useState<string>(params.coBrand?.name ?? "");
  const translate = useTranslate();

  useEffect(() => {
    dispatch(clearCommunityImageUrl());
  }, [dispatch]);

  const onSetImage = ({ url }: LocalFile) => dispatch(uploadImage({ image: url, type: "community" }));

  const image = uploadedImageUrls.community ?? (params.coBrand?.image_url as string | undefined);

  const onClick = () => {
    if (image === undefined) return;
    dispatch(deactivateModal());
    if (image === params.coBrand?.image_url && name === params.coBrand?.name) return;
    const id = params.coBrand?.id ?? createUuid();
    const community: Community & { image_url: string } = {
      id,
      image_url: image,
      name,
      privacy: "public",
      status: "Active",
    };
    dispatch(editCommunity(community));
    params.updateCoBrands(community);
  };

  const isValid = name.length > 0 && image !== undefined;
  const editMode = "coBrand" in params;

  return (
    <>
      <ModalHeader
        title={translate(
          editMode
            ? "app.create.collection.modal.edit_co_curator.title"
            : "app.create.collection.modal.create_co_curator.title",
        )}
      />
      <StyledContainer>
        <StyledTextInput
          label={translate("app.create.collection.modal.create_co_curator.curator_title")}
          name="curator-title"
          onChange={(event: ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
          value={name}
        />
        <div>
          <StyledTitle>{translate("app.create.collection.modal.create_co_curator.logo")}</StyledTitle>
          <HiddenImageUpload accept="image/*" onSetImage={onSetImage} ref={inputRef} />
          <UIButton fullwidth onClick={() => inputRef.current?.click()} variant="filled">
            {translate("app.profile.upload_image.title")}
          </UIButton>
        </div>
        {image === undefined || <StyledImage src={image} />}
        <Separator height={1} />
        <UIButton disabled={!isValid} fullwidth onClick={onClick} variant="filled">
          {translate(
            editMode
              ? "app.create.collection.modal.edit_co_curator.save"
              : "app.create.collection.modal.create_co_curator.save",
          )}
        </UIButton>
      </StyledContainer>
    </>
  );
};
